import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TreePine, RefreshCw, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getH1 } from '../seo/seoRoutes.mjs';
import { translations, DEFAULT_LANGUAGE } from '../utils/languages';

const PersonalGrowthSpreadPage = () => {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const [selectedCardCount, setSelectedCardCount] = useState(null);
  const [selectedGrowthType, setSelectedGrowthType] = useState(null);
  const [question, setQuestion] = useState('');
  const [currentQuote, setCurrentQuote] = useState('');

  // Card count options with their meanings for personal growth
  const cardOptions = [
    { count: 1, name: t('growth.singleInsight'), description: t('growth.singleInsightDesc') },
    { count: 3, name: t('growth.growthTriangle'), description: t('growth.growthTriangleDesc') },
    { count: 5, name: t('growth.personalDevelopmentCross'), description: t('growth.personalDevelopmentCrossDesc') },
    { count: 6, name: t('growth.lifeAspectsSpread'), description: t('growth.lifeAspectsSpreadDesc') },
    { count: 7, name: t('growth.chakraGrowth'), description: t('growth.chakraGrowthDesc') },
    { count: 10, name: t('growth.lifeJourneyCelticCross'), description: t('growth.lifeJourneyCelticCrossDesc') },
    { count: 12, name: t('growth.yearOfGrowth'), description: t('growth.yearOfGrowthDesc') }
  ];

  // Growth type options
  const growthTypes = [
    {
      type: "life-journey",
      name: t('growth.lifeJourney'),
      icon: "icon_08_enh_v2.png",
      color: "#D9D7F1",
      description: t('growth.lifeJourneyDesc'),
      focusAreas: [t('growth.lifePurpose'), t('growth.majorDecisions'), t('growth.lifeDirection'), t('growth.personalMission')]
    },
    {
      type: "hero-journey",
      name: t('growth.heroJourney'),
      icon: "icon_03_family_v2.png",
      color: "#FFFDDE",
      description: t('growth.heroJourneyDesc'),
      focusAreas: [t('growth.overcomingObstacles'), t('growth.innerStrength'), t('growth.personalTrials'), t('growth.transformation')]
    },
    {
      type: "spiritual-growth",
      name: t('growth.spiritualGrowth'),
      icon: "icon_01_find_v2.png",
      color: "#E7FBBE",
      description: t('growth.spiritualGrowthDesc'),
      focusAreas: [t('growth.spiritualAwakening'), t('growth.higherConsciousness'), t('growth.divineConnection'), t('growth.soulPurpose')]
    },
    {
      type: "transformation",
      name: t('growth.transformation'),
      icon: "icon_04_biz_v2.png",
      color: "#FFCBCB",
      description: t('growth.transformationDesc'),
      focusAreas: [t('growth.personalChange'), t('growth.breakingPatterns'), t('growth.newBeginnings'), t('growth.lifeRenewal')]
    },
    {
      type: "self-reflection",
      name: t('growth.selfReflection'),
      icon: "icon_05_col_v2.png",
      color: "#7BD3EA",
      description: t('growth.selfReflectionDesc'),
      focusAreas: [t('growth.selfAwareness'), t('growth.innerWisdom'), t('growth.personalInsights'), t('growth.selfDiscovery')]
    },
    {
      type: "self-improvement",
      name: t('growth.selfImprovement'),
      icon: "icon_10_rej_v2.png",
      color: "#D9D7F1",
      description: t('growth.selfImprovementDesc'),
      focusAreas: [t('growth.skillDevelopment'), t('growth.habitFormation'), t('growth.personalGoals'), t('growth.performanceGrowth')]
    },
    {
      type: "self-defeating",
      name: t('growth.selfDefeating'),
      icon: "icon_07_sh_v2.png",
      color: "#E7FBBE",
      description: t('growth.selfDefeatingDesc'),
      focusAreas: [t('growth.breakingLimitations'), t('growth.selfSabotagePatterns'), t('growth.negativeBeliefs'), t('growth.personalBarriers')]
    }
  ];

  // Spread interpretations using translation keys
  const getSpreadInterpretations = (count, growthType = 'lifeJourney') => {
    // Map UI growth types to translation key types
    const typeMapping = {
      'life-journey': 'lifeJourney',
      'hero-journey': 'heroJourney', 
      'spiritual-growth': 'spiritualGrowth',
      'transformation': 'transformation',
      'self-reflection': 'selfReflection',
      'self-improvement': 'selfImprovement',
      'self-defeating': 'selfDefeating'
    };
    
    const translationType = typeMapping[growthType] || 'lifeJourney';
    
    const positions = [];
    for (let i = 1; i <= count; i++) {
      const specificKey = `growth.spread.${translationType}.${count}.position${i}`;
      const baseKey = `growth.spread.base.${count}.position${i}`;
      const translation = translations[currentLanguage]?.[specificKey] || 
                         translations[currentLanguage]?.[baseKey] ||
                         translations[DEFAULT_LANGUAGE]?.[specificKey] ||
                         translations[DEFAULT_LANGUAGE]?.[baseKey] ||
                         `Card ${i}`;
      positions.push(translation);
    }
    
    return positions;
  };

  // Set random quote on component mount
    useEffect(() => {
    // Set a random quote when component mounts
    const growthQuotes = t('quotes.growth');
    const randomQuote = growthQuotes[Math.floor(Math.random() * growthQuotes.length)];
    setCurrentQuote(randomQuote);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  // Auto-scroll to growth type section when card count is selected
  useEffect(() => {
    if (selectedCardCount) {
      const growthSection = document.querySelector('.growth-type-section');
      if (growthSection) {
        growthSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  }, [selectedCardCount]);

  // Auto-scroll to question section when growth type is selected
  useEffect(() => {
    if (selectedGrowthType) {
      const questionSection = document.querySelector('.question-section');
      if (questionSection) {
        questionSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  }, [selectedGrowthType]);

  const startGrowthReading = () => {
    if (!selectedCardCount || !selectedGrowthType) return;
    
    const selectedType = growthTypes.find(type => type.type === selectedGrowthType);
    
    navigate('/spread-drawing', {
      state: {
        spreadName: `${selectedType.name} - ${cardOptions.find(o => o.count === selectedCardCount)?.name}`,
        cardCount: selectedCardCount,
        spreadType: "personal-growth",
        spreadInterpretations: getSpreadInterpretations(selectedCardCount, selectedGrowthType),
        question: question,
        tarotCards: [], // Use default cards from SpreadDrawingPage
        returnPath: "/personal-growth-spread",
        spreadConfig: {
          growthType: selectedGrowthType,
          isGrowthReading: true
        }
      }
    });
  };

  const resetSpread = () => {
    setSelectedCardCount(null);
    setSelectedGrowthType(null);
    setQuestion('');
    
    // Set new random quote
    const growthQuotes = t('quotes.growth');
    const randomQuote = growthQuotes[Math.floor(Math.random() * growthQuotes.length)];
    setCurrentQuote(randomQuote);
  };

  return (
    <div className="personal-growth-spread-page">
      {/* Welcome Section */}
      <section className="welcome-section">
        <div className="welcome-content">
          <div className="welcome-text">
            <h1 className="welcome-subtitle">
              {getH1('/personal-growth-spread', currentLanguage)}
            </h1>
            <blockquote className="inspiration-question">
              {currentQuote}
            </blockquote>
            <button 
              className="draw-cards-btn"
              onClick={() => {
                navigate('/spread-drawing', { 
                  state: { 
                    spreadName: t('dailyCard.growthSpreadName'),
                    spreadType: 'single',
                    cardCount: 1,
                    spreadInterpretations: [t('dailyCard.growthPosition')],
                    question: currentQuote,
                    returnPath: "/personal-growth-spread",
                    spreadConfig: {
                      isSingleCard: true,
                      isDailyCard: true
                    }
                  } 
                })
              }}
            >
              <Sparkles size={20} />
              {t('growth.drawGrowthCard')}
            </button>
          </div>
          <div className="welcome-decoration"></div>
        </div>
      </section>

      <div className="card-selection-section">
        <h2>1. {t('growth.chooseGrowthSpread')}</h2>
        <p className="selection-subtitle">{t('growth.selectCardCount')}</p>
        
        <div className="card-options-grid">
          {cardOptions.map((option) => (
            <div
              key={option.count}
              className={`card-option ${selectedCardCount === option.count ? 'selected' : ''}`}
              onClick={() => setSelectedCardCount(option.count)}
            >
              <span className="card-count">{option.count}</span>
              <div className="option-content">
                <span className="option-name">{option.name}</span>
                <p className="option-description">{option.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Growth Type Section */}
        <div className="growth-type-section">
          <h2>2. {t('growth.growthTypeTitle')}</h2>
          <p className="selection-subtitle">{t('growth.selectGrowthType')}</p>
          
          <div className="growth-types-grid">
            {growthTypes.map((growthType) => (
              <div
                key={growthType.type}
                className={`growth-type-card ${selectedGrowthType === growthType.type ? 'selected' : ''}`}
                onClick={() => setSelectedGrowthType(growthType.type)}
                style={{ '--type-color': growthType.color }}
              >
                <div className="type-icon">
                  <img 
                    src={`/images/${growthType.icon}`} 
                    alt={growthType.name}
                    style={{ width: '3rem', height: '3rem' }}
                  />
                </div>
                <h3 className="type-name">{growthType.name}</h3>
                <p className="type-description">{growthType.description}</p>
                <div className="focus-areas">
                  <small>{t('growth.focusAreas')}</small>
                  <ul>
                    {growthType.focusAreas.map((area, index) => (
                      <li key={index}>{area}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {selectedCardCount && selectedGrowthType && (
          <div className="question-section">
            <h2>3. {t('growth.focusIntention')}</h2>
            <p className="selection-subtitle">{t('growth.intentionDesc')}</p>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={t('growth.questionPlaceholder')}
              className="question-input"
              rows="3"
            />
            
            <button 
              onClick={startGrowthReading} 
              className="draw-cards-btn"
            >
              <TreePine size={20} />
              {t('growth.startDrawing')}
            </button>

            <button 
              onClick={resetSpread} 
              className="secondary-btn"
              style={{ marginLeft: '1rem' }}
            >
              <RefreshCw size={16} />
              {t('growth.reset')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalGrowthSpreadPage;
